import type { BlotterTableFilter } from '@talos/kyoko';
import {
  AccordionGroup,
  BLOTTER_TABLE_FILTERS_CONTAINER_ID,
  Box,
  MixpanelEvent,
  Tab,
  tabLabelerEnumerated,
  TabList,
  TabPanels,
  Tabs,
  TabSize,
  useCallbackRef,
  useConstant,
  useMixpanel,
  usePersistedTabs,
  usePortal,
  useTabs,
  type TabProps,
} from '@talos/kyoko';
import { useCallback, useState } from 'react';
import { useBlotterTableTabCloning } from '../../../../providers/AppConfigRedux/useBlotterTableTabCloning';
import { DEFAULT_LOAN_TABS, NEW_LOAN_TAB_DEFAULTS } from './defaults';
import { FilteredLoans } from './FilteredLoansBlotter';
import { LOAN_BLOTTER_PREFIX } from './tokens';

export interface LoanTabProps extends TabProps {
  defaultFilter: BlotterTableFilter;
  defaultColumns?: string[];
}

const tabLabeler = tabLabelerEnumerated('Loans');
function getBlotterID(tabID: string | undefined) {
  return `${LOAN_BLOTTER_PREFIX}/${tabID}`;
}

export function Loans() {
  const mixpanel = useMixpanel();
  const [initialFiltersOpen, setInitialFiltersOpen] = useState(false);

  const handleSelect = useCallbackRef(() => setInitialFiltersOpen(false));
  const handleAdd = useCallbackRef(() => {
    mixpanel.track(MixpanelEvent.AddTab);
    setInitialFiltersOpen(true);
  });

  const defaultInitialItems = useConstant(DEFAULT_LOAN_TABS);

  const persistedTabs = usePersistedTabs<LoanTabProps>(LOAN_BLOTTER_PREFIX, {
    defaultInitialItems,
    defaultInitialSelectedIndex: 0,
    onSelect: handleSelect,
  });

  const tabs = useTabs<LoanTabProps>({
    ...persistedTabs,
    showAddTab: true,
    tabLabeler,
    onAdd: handleAdd,
    onClone: useBlotterTableTabCloning({ getBlotterID }),
  });

  const handleCloneTab = useCallback(() => {
    tabs.clone(tabs.selectedIndex);
  }, [tabs]);

  const { setPortalRef: filtersContainerRef } = usePortal(BLOTTER_TABLE_FILTERS_CONTAINER_ID);

  return (
    <Box w="100%" h="100%">
      <Tabs {...tabs} h="100%" size={TabSize.Large}>
        <TabList isBordered rightItems={<Box ref={filtersContainerRef} />}>
          {tabs.items.map((tab, idx) => (
            <Tab key={idx} {...tab} reorderable={true} />
          ))}
        </TabList>
        <TabPanels style={{ flex: '1', display: 'flex', flexDirection: 'column' }}>
          {tabs.items.map((tab, idx) => {
            return (
              <AccordionGroup key={tab.id}>
                <FilteredLoans
                  key={idx}
                  blotterPersistID={`${LOAN_BLOTTER_PREFIX}/${tab.id}`}
                  tabLabel={tab.label}
                  defaultFilter={'defaultFilter' in tab ? tab.defaultFilter : NEW_LOAN_TAB_DEFAULTS.defaultFilter}
                  onCloneTab={handleCloneTab}
                  initialIsOpen={initialFiltersOpen}
                />
              </AccordionGroup>
            );
          })}
        </TabPanels>
      </Tabs>
    </Box>
  );
}

export default Loans;
